import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Logger } from '@wix/web-bi-logger/dist/src/types';
import { i18n as i18nType } from 'i18next';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
  when,
} from 'mobx';
import { AccountSettingsApi } from '../services/accountSettingsApi';
import { DealerApi } from '../services/dealerApi';
import { NotificationStore } from '../utils/notification';
import { AccountRecoveryCodeStore } from './accountRecoveryCode';
import { AccountVerificationStore } from './accountVerification';
import { PhoneStore } from './phone';
import { ApiStore } from './api';
import { AppleLoginStore } from './appleLogin';
import { ApproveAndContinueStore } from './approveAndContinue';
import { CaptchaStore } from './captcha';
import { ChangeAccountEmailStore } from './changeAccountEmail';
import { ConfirmDetailsStore } from './confirmDetails';
import { ConfirmEmailStore } from './confirmEmail';
import { DisplayStore } from './display';
import { EmailStepStore } from './emailStep';
import { FacebookLoginStore } from './facebookLogin';
import { GoogleLoginStore } from './googleLogin';
import { LoginStore } from './login';
import { ManagePasswordStore } from './managePassword';
import { NavigationStore } from './navigation';
import { PostLoginStore } from './postLogin';
import { PostSocialLoginEnterEmailStore } from './postSocialLoginEnterEmail';
import { ResetPasswordStore } from './resetPassword';
import { ResetPasswordInvalidStore } from './resetPasswordInvalid';
import { SelectAccountStore } from './selectAccount';
import { SelectWorkspaceStore } from './selectWorkspace';
import { SessionId } from './sessionId';
import { SsoStore } from './sso';
import { SsoEmailAlreadyExistStore } from './ssoEmailAlreadyExist';
import { SsoEnterEmailStore } from './ssoEnterEmail';
import { StepUpAuthStore } from './stepUpAuth';
import { TagManagerStore } from './tagManager';
import { TwoFactorAuthStore } from './twoFactorAuth';
import { UserDataStore } from './userData';
import { WebAuthnStore } from './webauthn';
import { PoliciesStore } from './policies';
import { SignupStore } from './signup';
import { SocialAuthStore } from './socialAuth';
import { loginPageOnStart, loginClickOnBack } from '@wix/bi-logger-hls2/v2';
import Experiments from '@wix/wix-experiments/';
import { DeviceLoginStore } from './deviceLogin';
import { DeviceAuthorizeStore } from './deviceAuthorize';
import { ModalModeHandlerStore } from './modalModeHandler';
import {
  LOGIN_PAGE_CONTEXT,
  SEARCH_PARAMS,
  SIGNUP_PAGE_CONTEXT,
  IFRMAE_EVENTS,
} from '../utils/constants';
import { ROUTES } from '../routes';
import { PasswordService } from '../services/PasswordServiceApi';
import { PasswordStrengthExtractor } from '@wix/identity-fed-common';
import { BlockedAccountStore } from './blockedAccount';
import { PasskeyEnrollStore } from './passkeyEnroll';
import { BiProfileServiceApi } from '../services/biProfileApi';
import { createBiLogger } from '../services/biLogger';
import { PanoramaService } from '../services/panorama';
import { LoginEnvironment } from '../platform/environment';
import { PostSignupEmailConfirmStore } from './postSignupEmailConfirm';
import { SignupEmailFactorStore } from './signupEmailFactor';
import { PosDeviceLoginStore } from './posDeviceLogin';
import { IdpConnectionLoginStore } from './idpConnectionLogin';
import { Web5DeviceLoginStore } from './web5DeviceLogin';
import { EmailVerificationResultStore } from './emailVerificationResult';

const RouteToContextTypeMapper = {
  [ROUTES.SELECT_ACCOUNT]: 'select-account',
  [ROUTES.SIGNUP_PASSWORD]: 'signup-password',
  [ROUTES.LOGIN_PASSWORD]: 'login-password',
  [ROUTES.SOCIAL_ONLY]: 'social-login',
  [ROUTES.GOOGLE_LOGIN]: 'google-onetap-login',
  [ROUTES.DEVICE_LOGIN]: 'device-login',
  [ROUTES.SSO_ENTER_EMAIL]: 'sso-enter-email',
  [ROUTES.SSO_MAIN]: 'sso-landing-page',
  [ROUTES.ACCOUNT_VERIFICATION]: 'social-account-verification',
};

const RouteToContextMapper = {
  [ROUTES.SSO_ENTER_EMAIL]: LOGIN_PAGE_CONTEXT,
  [ROUTES.SSO_MAIN]: LOGIN_PAGE_CONTEXT,
  [ROUTES.SELECT_WORKSPACE]: 'select-workspace',
  [ROUTES.SIGNUP_PASSWORD]: SIGNUP_PAGE_CONTEXT,
  [ROUTES.LOGIN_PASSWORD]: LOGIN_PAGE_CONTEXT,
  [ROUTES.GOOGLE_LOGIN]: LOGIN_PAGE_CONTEXT,
  [ROUTES.SOCIAL_ONLY]: LOGIN_PAGE_CONTEXT,
  [ROUTES.POST_LOGIN]: 'post-login',
  [ROUTES.RESET_PASSWORD]: 'reset-password',
  [ROUTES.RESET_PASSWORD_INVALID]: 'reset-password-expired',
  [ROUTES.EMAIL_VERIFICATION_RESULT]: 'email-verification',
  [ROUTES.STEP_UP_INITIATION]: 'step-up',
  [ROUTES.STEP_UP]: 'step-up',
  [ROUTES.DEVICE_LOGIN]: LOGIN_PAGE_CONTEXT,
  [ROUTES.SELECT_ACCOUNT]: LOGIN_PAGE_CONTEXT,
  [ROUTES.ACCOUNT_VERIFICATION]: LOGIN_PAGE_CONTEXT,
};

/** The subset of the `wixRecorder` SDK global that RootStore uses. */
interface WixRecorder {
  __forceRecording?: () => Promise<void>;
  addLabel: (label: string) => void;
  addCustomAttribute: (key: string, value: string | string[]) => void;
  withExperiments: (experiments: unknown) => void;
}

/**
 * Everything RootStore is constructed with. The `env` (LoginEnvironment) is the
 * SSR seam: the store reads the browser only through it. `history` is NOT here
 * yet — NavigationStore still owns `createBrowserHistory`; it moves into these
 * deps when the Navigation group migrates.
 */
export interface RootStoreDeps {
  env: LoginEnvironment;
  i18n: i18nType;
  experiments: Experiments;
  accountSettingsApi: AccountSettingsApi;
  biProfileServiceApi: BiProfileServiceApi;
  dealerApi: DealerApi;
  passwordStrengthExtractor: PasswordStrengthExtractor;
}

export class RootStore {
  public env: LoginEnvironment;
  private _panorama?: PanoramaService;
  private _biLogger?: Logger;
  private _started = false;
  public navigationStore: NavigationStore;
  public ssoEnterEmailStore: SsoEnterEmailStore;
  public postSocialLoginEnterEmailStore: PostSocialLoginEnterEmailStore;
  public resetPasswordStore: ResetPasswordStore;
  public resetPasswordInvalidStore: ResetPasswordInvalidStore;
  public ssoStore: SsoStore;
  public userDataStore: UserDataStore;
  public approveAndContinueStore: ApproveAndContinueStore;
  public apiStore: ApiStore;
  public ssoEmailAlreadyExistStore: SsoEmailAlreadyExistStore;
  public displayStore: DisplayStore;
  public passwordStrengthExtractor: PasswordStrengthExtractor;
  public selectAccountStore: SelectAccountStore;
  public selectWorkspaceStore: SelectWorkspaceStore;
  public emailStepStore: EmailStepStore;
  public loginStore: LoginStore;
  public signupStore: SignupStore;
  public captchaStore: CaptchaStore;
  public tagManagerStore: TagManagerStore;
  public twoFactorAuthStore: TwoFactorAuthStore;
  public accountRecoveryCodeStore: AccountRecoveryCodeStore;
  public stepUpAuthStore: StepUpAuthStore;
  public appleLoginStore: AppleLoginStore;
  public facebookLoginStore: FacebookLoginStore;
  public googleLoginStore: GoogleLoginStore;
  public accountVerificationStore: AccountVerificationStore;
  public sessionId: SessionId;
  public clientDeviceId: string;
  public confirmEmailStore: ConfirmEmailStore;
  public postSignupEmailConfirmStore: PostSignupEmailConfirmStore;
  public signupEmailFactorStore: SignupEmailFactorStore;
  public changeAccountEmailStore: ChangeAccountEmailStore;
  public confirmDetailsStore: ConfirmDetailsStore;
  public managePasswordStore: ManagePasswordStore;
  public phoneStore: PhoneStore;
  public passkeyEnrollStore: PasskeyEnrollStore;
  public blockedAccountStore: BlockedAccountStore;
  public emailVerificationResultStore: EmailVerificationResultStore;
  public isLoaded: boolean = false;
  public isLoading: boolean = false;
  public activeSocialProvider: string;
  public accountSettingsApi: AccountSettingsApi;
  public biProfileServiceApi: BiProfileServiceApi;
  public dealerApi: DealerApi;
  public notificationStore: NotificationStore;
  public postLoginStore: PostLoginStore;
  public policiesStore: PoliciesStore;
  public socialAuthStore: SocialAuthStore;
  public modalModeHandlerStore: ModalModeHandlerStore;
  public webauthnStore: WebAuthnStore;
  public passwordService: PasswordService;
  public i18n: i18nType;
  public i18nLoaded: (i18n?: any) => Promise<any>;
  public locale: string;
  public experiments: Experiments;
  public deviceLoginStore: DeviceLoginStore;
  public deviceAuthorizeStore: DeviceAuthorizeStore;
  public posDeviceLoginStore: PosDeviceLoginStore;
  public idpConnectionLoginStore: IdpConnectionLoginStore;
  public web5DeviceLoginStore: Web5DeviceLoginStore;
  public isNewLoginApp: boolean = false;
  public originUrl?: string;
  public userType: string;
  public isStudio: boolean = false;

  /**
   * Panorama (client monitoring) touches `window` when its client is built, so
   * it is created lazily on first use — which on the client is `start()`, and
   * on the server never (a server render calls neither `start()` nor any
   * transaction). Kept settable: tests inject a stub panorama.
   */
  public get panorama(): PanoramaService {
    return (this._panorama ??= new PanoramaService(
      this.env.renderModel.staticsUrl,
    ));
  }
  public set panorama(panorama: PanoramaService) {
    this._panorama = panorama;
  }

  /**
   * The BI logger is created lazily on first use (same window reason). Several
   * login/signup/social stores swap in a fresh logger mid-flow, and tests
   * inject one, so the setter is kept.
   */
  public get biLogger(): Logger {
    return (this._biLogger ??= createBiLogger());
  }
  public set biLogger(biLogger: Logger) {
    this._biLogger = biLogger;
  }

  constructor(deps: RootStoreDeps) {
    const {
      env,
      i18n,
      experiments,
      accountSettingsApi,
      biProfileServiceApi,
      dealerApi,
      passwordStrengthExtractor,
    } = deps;

    makeObservable(this, {
      isLoaded: observable,
      isLoading: observable,
      activeSocialProvider: observable,
      isNewLoginApp: observable,
      isFireAppStartReady: computed,
      onHeaderBackButtonClicked: action.bound,
      onClickRootElement: action.bound,
      setActiveSocialProvider: action.bound,
      locale: observable,
      start: action,
      setIsLoading: action,
      initStores: action,
      initPostLoginStores: action.bound,
    });

    this.env = env;
    this.i18n = i18n;
    this.passwordStrengthExtractor = passwordStrengthExtractor;
    this.accountSettingsApi = accountSettingsApi;
    this.biProfileServiceApi = biProfileServiceApi;
    this.dealerApi = dealerApi;
    this.experiments = experiments;
    this.locale = env.renderModel.locale;
    this.isNewLoginApp = env.renderModel.isNewLogin;
    this.passwordService = new PasswordService();

    // Constructor is synchronous, does no I/O and touches no browser global, so
    // it is safe on the server. Everything client-only — page-load reporting,
    // listeners, SDK reads, the LOADED post-message, render-param backfill —
    // lives in start(), which AppLoader calls from useEffect.
    this.setUserType();
    this.initStores();
    this.originUrl = env.referrer
      ? env.referrer.split('?').shift()
      : this.navigationStore.originUrl;
  }

  /**
   * Client-only lifecycle, called once from AppLoader's useEffect after the
   * first render (and after hydration when server-rendered). Idempotent: React
   * 18 StrictMode mounts effects twice in dev, and loginPageOnStart must fire
   * exactly once.
   */
  public async start() {
    if (this._started) {
      return;
    }
    this._started = true;

    this.panorama.reportLoadStart();
    this.panorama.reportLoadFinish();
    void this.initMissingRenderParams();
    void this.initClientDeviceId();
    runInAction(async () => {
      this.isLoaded = true;
      this.env.postMessageToParent({ id: IFRMAE_EVENTS.LOADED });
      await when(() => this.isFireAppStartReady);
      const isEmailStepRoute =
        this.navigationStore.currentRoute === ROUTES.EMAIL_STEP;
      const context = isEmailStepRoute
        ? this.emailStepStore.getContext()
        : RouteToContextMapper[this.navigationStore.currentRoute];
      const contextType = isEmailStepRoute
        ? context
        : RouteToContextTypeMapper[this.navigationStore.currentRoute];
      const viewport = this.env.viewport;
      this.biLogger.report(
        loginPageOnStart({
          origin: this.navigationStore.originUrl,
          context,
          context_type: contextType,
          referrer: this.navigationStore.originContext,
          referrer_context: this.navigationStore.loginDialogContext,
          context_button: this.navigationStore.getQueryParam('loginCompName'),
          avail_height: viewport?.height ?? 0,
          avail_width: viewport?.width ?? 0,
          screen_height: viewport?.screen.height ?? 0,
          screen_width: viewport?.screen.width ?? 0,
        }),
      );
    });
  }

  public async initClientDeviceId() {
    const fingerprinter = await FingerprintJS.load({ monitoring: false });
    this.clientDeviceId = (await fingerprinter.get()).visitorId;
  }

  public async initMissingRenderParams() {
    try {
      const missingParams = await this.apiStore.getMissingRenderParams();
      // Backfill the render model with server-resolved SSO / logged-in-email
      // params. Writes into env.renderModel instead of window.__*__: consumers
      // (sso, userData) read these at construction, which is before this
      // fire-and-forget call resolves, so it only ever fills the render model
      // for readers that consult it after load — the same net effect as before.
      const renderModel = this.env.renderModel;
      renderModel.ssoRedirectUrl =
        renderModel.ssoRedirectUrl || missingParams.ssoRedirectUrl;
      renderModel.ssoExistingWixAccountEmail =
        renderModel.ssoExistingWixAccountEmail ||
        missingParams.ssoExistingWixAccountEmail;
      renderModel.ssoAccountName =
        renderModel.ssoAccountName || missingParams.ssoAccountName;
      renderModel.ssoAccountImage =
        renderModel.ssoAccountImage || missingParams.ssoAccountImage;
      renderModel.ssoAccountId =
        renderModel.ssoAccountId || missingParams.ssoAccountId;
      renderModel.currentLoggedInUserEmail =
        renderModel.currentLoggedInUserEmail ||
        missingParams.currentLoggedInUserEmail;
    } catch (error) {
      console.error(error);
    }
  }

  public initStores() {
    this.apiStore = new ApiStore(this);
    this.notificationStore = new NotificationStore();
    this.navigationStore = new NavigationStore(this);
    this.isStudio =
      this.navigationStore.getQueryParam(SEARCH_PARAMS.IS_STUDIO) === 'true';
    this.sessionId = new SessionId(this);
    this.displayStore = new DisplayStore(this);
    this.ssoEnterEmailStore = new SsoEnterEmailStore(this);
    this.postSocialLoginEnterEmailStore = new PostSocialLoginEnterEmailStore(
      this,
    );

    this.userDataStore = new UserDataStore(this);
    this.ssoStore = new SsoStore(this);
    this.approveAndContinueStore = new ApproveAndContinueStore(this);
    this.ssoEmailAlreadyExistStore = new SsoEmailAlreadyExistStore(this);
    this.selectAccountStore = new SelectAccountStore(this);
    this.selectWorkspaceStore = new SelectWorkspaceStore(this);
    this.socialAuthStore = new SocialAuthStore(this);
    this.emailStepStore = new EmailStepStore(this);
    this.loginStore = new LoginStore(this);
    this.signupStore = new SignupStore(this);
    this.captchaStore = new CaptchaStore(this);
    this.tagManagerStore = new TagManagerStore(this);
    this.twoFactorAuthStore = new TwoFactorAuthStore(this);
    this.accountRecoveryCodeStore = new AccountRecoveryCodeStore(this);
    this.stepUpAuthStore = new StepUpAuthStore(this);
    this.googleLoginStore = new GoogleLoginStore(this);
    this.appleLoginStore = new AppleLoginStore(this);
    this.facebookLoginStore = new FacebookLoginStore(this);
    this.accountVerificationStore = new AccountVerificationStore(this);
    this.postLoginStore = new PostLoginStore(this);
    this.policiesStore = new PoliciesStore(this);
    this.deviceLoginStore = new DeviceLoginStore(this);
    this.deviceAuthorizeStore = new DeviceAuthorizeStore(this);
    this.posDeviceLoginStore = new PosDeviceLoginStore(this);
    this.idpConnectionLoginStore = new IdpConnectionLoginStore(this);
    this.web5DeviceLoginStore = new Web5DeviceLoginStore(this);
    this.modalModeHandlerStore = new ModalModeHandlerStore(this);
    this.managePasswordStore = new ManagePasswordStore(this);
    this.resetPasswordStore = new ResetPasswordStore(this);
    this.resetPasswordInvalidStore = new ResetPasswordInvalidStore(this);
    this.blockedAccountStore = new BlockedAccountStore(this);
    this.emailVerificationResultStore = new EmailVerificationResultStore(this);
    this.postSignupEmailConfirmStore = new PostSignupEmailConfirmStore(this);
    this.signupEmailFactorStore = new SignupEmailFactorStore(this);
    this.webauthnStore = new WebAuthnStore(this);
  }

  public initPostLoginStores() {
    this.phoneStore = new PhoneStore(this);
    this.confirmEmailStore = new ConfirmEmailStore(this);
    this.changeAccountEmailStore = new ChangeAccountEmailStore(this);
    this.confirmDetailsStore = new ConfirmDetailsStore(this);
    this.passkeyEnrollStore = new PasskeyEnrollStore(this);
  }

  public setIsLoading(state: boolean) {
    this.isLoading = state;
  }

  public setActiveSocialProvider(provider: string) {
    this.activeSocialProvider = provider;
  }

  get language() {
    return {
      lang: this.i18n.languages?.[1],
      locale: this.i18n.language,
    };
  }

  public getActiveSocialProviderStore() {
    const providerStores: any = {
      google: this.googleLoginStore,
      facebook: this.facebookLoginStore,
      apple: this.appleLoginStore,
    };

    return providerStores[this.activeSocialProvider];
  }

  private setUserType() {
    const { userType, basename } = this.env.renderModel;
    if (userType) {
      this.userType = userType ?? '';
      return;
    }
    if (basename) {
      // for verticals should set baseURL to include the userType
      const baseUrlParts = basename.split('/').filter((e) => e);
      this.userType = baseUrlParts.length > 1 ? baseUrlParts[0] : '';
    }
  }

  public async triggerWixRecorder() {
    if (!this.experiments.enabled('specs.ident.NewLoginUseWixRecorder')) {
      return;
    }
    const wixRecorder = this.env.global<WixRecorder>('wixRecorder');
    if (this.experiments.enabled('specs.ident.NewLoginForceWixRecorder')) {
      await wixRecorder?.__forceRecording?.();
    }
    wixRecorder?.addLabel('users-new-login');
    this.addAttributeToWixRecorder(
      'isUserLoggedIn',
      (!!this.userDataStore.currentLoggedInEmail).toString(),
    );
    this.addAttributeToWixRecorder('referrer', this.env.referrer);
    wixRecorder?.withExperiments(this.experiments.all());
  }

  // TODO - Delete when finish investigating new login failure
  onClickRootElement(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (this.experiments.enabled('specs.ident.NewLoginForceWixRecorder')) {
      const _e = e?.target as any;
      this.addAttributeToWixRecorder('pageClicked', [
        _e?.className,
        _e?.innerText,
      ]);
    }
  }

  addAttributeToWixRecorder(key: string, value: string | string[]) {
    if (!this.experiments.enabled('specs.ident.NewLoginUseWixRecorder')) {
      return;
    }
    this.env
      .global<WixRecorder>('wixRecorder')
      ?.addCustomAttribute(`newLogin#${key}`, value);
  }

  onHeaderBackButtonClicked(callback?: Function) {
    this.biLogger.report(
      loginClickOnBack({
        step_name: this.navigationStore.currentRoute,
      }),
    );
    if (callback) {
      return callback();
    }
    this.navigationStore.goBack();
  }

  get isFireAppStartReady() {
    return !this.emailStepStore.isFetchingByDefaultEmail;
  }
}
